import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  Circle,
  CircleDot,
  Compass,
} from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';

import { StudioLayout } from '@/shared/layout/studio-layout';
import { pathLabel } from '@/shared/program/path-label';
import { ACTIVITY_KIND, activityHref, formatMinutes } from '@/shared/ui/activity/activity-kind';
import { buttonVariants } from '@/shared/ui/button-variants';

import type { StudioModel, StudioModule } from '../api/use-studio';
import type { KeyboardEvent } from 'react';

import './studio-module-page.css';

const TABS = ['Activities', 'Module brief', 'Resources'] as const;
type ModuleTab = (typeof TABS)[number];
const STATUS_LABEL = {
  complete: 'Complete',
  in_progress: 'In progress',
  not_started: 'Not started',
};

function ModuleOverview({ module, studio }: { module: StudioModule; studio: StudioModel }) {
  const [tab, setTab] = useState<ModuleTab>('Activities');
  const [day, setDay] = useState<number | 'all'>('all');
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const controls = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLElement>(null);
  const id = useId();
  const days = [...new Set(module.items.map((item) => item.day))].sort((a, b) => a - b);
  const items = day === 'all' ? module.items : module.items.filter((item) => item.day === day);
  const complete = module.items.length > 0 && module.done === module.items.length;
  const next =
    module.items.find((item) => item.status === 'in_progress') ??
    module.items.find((item) => item.status !== 'complete') ??
    module.items[0];
  const previousModule = studio.modules.find((item) => item.number === module.number - 1);
  const nextModule = studio.modules.find((item) => item.number === module.number + 1);
  const action = complete
    ? 'Review module'
    : next?.status === 'in_progress'
      ? 'Continue module'
      : module.done > 0
        ? 'Next activity'
        : 'Start module';

  useLayoutEffect(() => {
    const scroller = panel.current?.closest<HTMLElement>('[data-studio-scroll]');
    if (!scroller || !panel.current || !controls.current) return;
    // A new tab or day starts below the sticky controls, even after a long reading list.
    const obscured =
      controls.current.getBoundingClientRect().bottom - panel.current.getBoundingClientRect().top;
    if (obscured > 0) scroller.scrollTop = Math.max(0, scroller.scrollTop - obscured);
  }, [tab, day]);

  function moveTab(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let target: number;
    if (event.key === 'ArrowRight') target = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') target = (index + TABS.length - 1) % TABS.length;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = TABS.length - 1;
    else return;
    event.preventDefault();
    const targetTab = TABS[target];
    if (targetTab) setTab(targetTab);
    tabs.current[target]?.focus();
  }

  return (
    <div className="module-workspace">
      <header className="module-overview-header">
        <div className="module-location">
          <Link to="/studio" className="module-library-link" aria-label="Back to Studio">
            <ArrowLeft size={14} aria-hidden="true" /> Studio
          </Link>
          <span className="module-location-divider" aria-hidden="true">
            /
          </span>
          <span>Module {module.number}</span>
          <nav aria-label="Module navigation" className="module-navigation">
            {previousModule ? (
              <Link
                to="/studio/modules/$number"
                params={{ number: String(previousModule.number) }}
                aria-label={`Previous module: ${previousModule.title}`}
                title={`Module ${previousModule.number}: ${previousModule.title}`}
              >
                <ArrowLeft size={14} aria-hidden="true" />
              </Link>
            ) : null}
            <span>
              {module.number} / {studio.totalModules}
            </span>
            {nextModule ? (
              <Link
                to="/studio/modules/$number"
                params={{ number: String(nextModule.number) }}
                aria-label={`Next module: ${nextModule.title}`}
                title={`Module ${nextModule.number}: ${nextModule.title}`}
              >
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ) : null}
          </nav>
        </div>
        <h1>{module.title}</h1>
        <div className="module-facts">
          <span>{module.items.length} activities</span>
          <span>{module.durationDays} planned days</span>
          {complete || module.status === 'current' ? (
            <span className="module-state">{complete ? 'Completed' : 'Current module'}</span>
          ) : null}
          {pathLabel(module.path) ? (
            <span className="module-path" data-role={module.path}>
              {pathLabel(module.path)}
            </span>
          ) : null}
        </div>
        <p className="module-outcome">{module.outcome}</p>
        <div className="module-action-bar">
          <div className="module-completion">
            <div className="module-completion-label">
              <strong>
                {module.done} / {module.items.length}
              </strong>{' '}
              complete
            </div>
            <div
              className="module-progress-segments"
              role="progressbar"
              aria-label={`Module ${module.number} completion`}
              aria-valuemin={0}
              aria-valuemax={module.items.length || 1}
              aria-valuenow={module.done}
              aria-valuetext={`${module.done} of ${module.items.length} activities complete`}
            >
              {module.items.map((item) => (
                <span key={item.id} data-status={item.status} />
              ))}
            </div>
          </div>
          {next ? (
            <Link
              to={activityHref(next.kind, next.id)}
              className={buttonVariants({ size: 'sm' })}
              title={next.title}
            >
              {action} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          ) : null}
        </div>
        {module.path === 'optional' ? (
          <div className="module-path-note" role="note">
            <p>
              <strong>Not on your path.</strong> You can start it anyway; the label only reflects
              your answers.
            </p>
            <Link
              to="/studio/path"
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              <Compass size={13} strokeWidth={1.75} aria-hidden="true" /> Change my path
            </Link>
          </div>
        ) : null}
      </header>

      <div ref={controls} className="module-controls">
        <div role="tablist" aria-label="Module overview" className="module-view-tabs">
          {TABS.map((name, index) => (
            <button
              key={name}
              ref={(element) => {
                tabs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`${id}-tab-${index}`}
              aria-controls={tab === name ? `${id}-panel-${index}` : undefined}
              aria-selected={tab === name}
              tabIndex={tab === name ? 0 : -1}
              onClick={() => setTab(name)}
              onKeyDown={(event) => moveTab(event, index)}
            >
              {name}
              {name === 'Activities' ? <span>{module.items.length}</span> : null}
              {name === 'Resources' ? <span>{module.readings.length}</span> : null}
            </button>
          ))}
        </div>
        {tab === 'Activities' ? (
          <div className="module-day-filter" role="group" aria-label="Filter activities by day">
            <button type="button" aria-pressed={day === 'all'} onClick={() => setDay('all')}>
              All days
            </button>
            {days.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={day === value}
                onClick={() => setDay(value)}
              >
                Day {value}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <section
        ref={panel}
        key={tab}
        role="tabpanel"
        id={`${id}-panel-${TABS.indexOf(tab)}`}
        aria-labelledby={`${id}-tab-${TABS.indexOf(tab)}`}
        tabIndex={0}
        className="module-view-panel"
      >
        {tab === 'Activities' ? (
          <>
            <div className="module-activity-caption">
              <span role="status" aria-live="polite">
                {day === 'all' ? 'Full module' : `Day ${day}`} · {items.length} activities
              </span>
              <span>Lessons and practical workshops</span>
            </div>
            <ol className="module-activity-list" aria-label="Module activities">
              {items.map((item, index) => {
                const startsDay = index === 0 || items[index - 1]?.day !== item.day;
                const kind = ACTIVITY_KIND[item.kind];
                const ActivityIcon = kind.icon;
                const duration = formatMinutes(item.estimatedMinutes);
                const StatusIcon =
                  item.status === 'complete'
                    ? Check
                    : item.status === 'in_progress'
                      ? CircleDot
                      : Circle;
                return (
                  <li key={item.id} data-starts-day={startsDay}>
                    <Link
                      to={activityHref(item.kind, item.id)}
                      className="module-activity-row"
                      data-current={!complete && item.id === next?.id}
                    >
                      <span className="module-activity-symbol" aria-hidden="true">
                        <ActivityIcon size={18} />
                      </span>
                      <span className="module-activity-copy">
                        <span className="module-activity-title">{item.title}</span>
                        <span className="module-activity-details">
                          <span className="module-activity-kind">{kind.label}</span>
                          <span>Day {item.day}</span>
                          {duration ? <span>{duration}</span> : null}
                        </span>
                        {item.summary ? (
                          <span className="module-activity-summary">{item.summary}</span>
                        ) : null}
                      </span>
                      <span
                        className="module-activity-status"
                        data-status={item.status}
                        title={STATUS_LABEL[item.status]}
                      >
                        <StatusIcon size={15} aria-hidden="true" />
                        <span>{STATUS_LABEL[item.status]}</span>
                      </span>
                      <ArrowUpRight
                        size={14}
                        className="module-activity-arrow"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                );
              })}
            </ol>
            {items.length === 0 ? (
              <p className="module-empty-message">No activities scheduled yet.</p>
            ) : null}
          </>
        ) : null}
        {tab === 'Module brief' ? (
          <div className="module-brief">
            <section>
              <h2>What you’ll learn</h2>
              <p>{module.outcome}</p>
            </section>
            {module.whyThisModule ? (
              <section>
                <h2>Why this module matters</h2>
                <p>{module.whyThisModule}</p>
              </section>
            ) : null}
            {module.placement ? (
              <>
                <section>
                  <h2>Who this is for</h2>
                  <p>{module.placement.audience}</p>
                </section>
                {module.placement.prerequisites.length > 0 ? (
                  <section>
                    <h2>Before you start</h2>
                    <ul className="module-brief-list">
                      {module.placement.prerequisites.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                <section>
                  <h2>You can skip this if</h2>
                  <p>{module.placement.skipWhen}</p>
                </section>
              </>
            ) : null}
            {module.mioStance ? (
              <aside className="module-mio-note">
                <span aria-hidden="true">m.</span>
                <div>
                  <h2>Working with Mio</h2>
                  <p>{module.mioStance}</p>
                </div>
              </aside>
            ) : null}
          </div>
        ) : null}
        {tab === 'Resources' ? (
          <>
            <p className="module-resources-caption">
              Primary documentation, specifications and papers for this module.
            </p>
            <ul className="module-resource-list">
              {module.readings.map((reading, index) => {
                const content = (
                  <>
                    <span className="module-resource-number">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="module-resource-copy">
                      <strong>{reading.title}</strong>
                      {reading.author ? <span>{reading.author}</span> : null}
                      {reading.note ? <span>{reading.note}</span> : null}
                    </span>
                  </>
                );
                return (
                  <li key={`${reading.title}-${index}`}>
                    {reading.url ? (
                      <a href={reading.url} target="_blank" rel="noreferrer">
                        {content}
                        <ArrowUpRight size={14} aria-hidden="true" />
                        <span className="sr-only">Opens in a new tab</span>
                      </a>
                    ) : (
                      <div>{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
            {module.readings.length === 0 ? (
              <p className="module-empty-message">Readings are included inside each lesson.</p>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
}

export function StudioModulePage({ number }: { number: number }) {
  return (
    <StudioLayout activeId={null} subtitle={`Module ${number}`} scrollKey={`module-${number}`}>
      {(studio) => {
        const module = studio.modules.find((item) => item.number === number);
        return module ? (
          <ModuleOverview key={number} module={module} studio={studio} />
        ) : (
          <div className="module-workspace module-not-found">
            <h1>Module {number} isn’t in this curriculum.</h1>
            <Link to="/studio">
              <ArrowLeft size={14} aria-hidden="true" /> Back to Studio
            </Link>
          </div>
        );
      }}
    </StudioLayout>
  );
}
